import VisibilityScope, { VisibilityScopeValue } from "../../../../ui/VisibilityScope";
import { FC, useContext, useState } from "react";
import { Controller } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import NotificationDispatch, { showErrorNotification } from "../../../../../context/notificationContext";
import useI18n from "../../../../../hooks/useTranslations";
import { InsertNormativeBody } from "../../../../../http/border/types/normatives";
import { useNormativesApi } from "../../../../../http/border/useNormatives";
import Form from "../../../../ui/Form";
import FormField from "../../../../ui/FormField";
import FormFieldsContainer from "../../../../ui/FormFieldsContainer";
import Input from "../../../../ui/Input";
import Numeric from "../../../../ui/Numeric";
import Switch from "../../../../ui/Switch";
import Textarea from "../../../../ui/Textarea";

interface InsertNormativeForm {
  name: string | null;
  description: string | null;
  order: number | null;
  visibility: VisibilityScopeValue;
  reinforcedRailRequired: boolean;
}

const NewNormative: FC = () => {
  const { systemId } = useParams<{ systemId: string }>();
  const navigate = useNavigate();
  const i18n = useI18n();
  const dispatch = useContext(NotificationDispatch);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const normativesApi = useNormativesApi();

  const onSubmit = () => async (formData: InsertNormativeForm) => {
    const body: InsertNormativeBody = {
      name: formData.name!,
      description: formData.description!,
      descriptionIt: null,
      descriptionDe: null,
      descriptionEs: null,
      descriptionFr: null,
      order: formData.order!,
      visibility: formData.visibility,
      reinforcedRailRequired: formData.reinforcedRailRequired,
    };

    setIsSubmitting(true);

    try {
      const res = await normativesApi.insertAdminNormative(body);
      navigate(`/admin/systems/${systemId}/normatives/${res.id}`);
    } catch (error) {
      dispatch(showErrorNotification(i18n, error));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Form<InsertNormativeForm>
      defaultValues={{
        name: null,
        description: null,
        order: 50,
        visibility: "hidden",
        reinforcedRailRequired: false,
      }}
      submitText={i18n.translation.common.save}
      disabled={isSubmitting}
      onSubmit={onSubmit}
    >
      {({ register, formState: { errors }, control, setValue, watch }) => {
        const reinforcedRailRequired = !!watch("reinforcedRailRequired");

        return (
          <FormFieldsContainer fullWidth>
            <FormField label={i18n.translation.common.name} error={errors.name}>
              {({ labelId, isInvalid, isOptional }) => (
                <Input
                  id={labelId}
                  isInvalid={isInvalid}
                  disabled={isSubmitting}
                  {...register("name", {
                    required: !isOptional,
                    minLength: 1,
                  })}
                />
              )}
            </FormField>
            <FormField label={i18n.translation.common.description} error={errors.description}>
              {({ labelId, isInvalid, isOptional }) => (
                <Textarea
                  id={labelId}
                  isInvalid={isInvalid}
                  disabled={isSubmitting}
                  {...register("description", {
                    required: !isOptional,
                    minLength: 1,
                  })}
                />
              )}
            </FormField>
            <FormField label={i18n.translation.common.reinforcedRailRequired} isOptional>
              {({ labelId }) => (
                <Switch
                  id={labelId}
                  checked={reinforcedRailRequired}
                  disabled={isSubmitting}
                  onChange={(checked) => setValue("reinforcedRailRequired", checked)}
                />
              )}
            </FormField>
            <Controller
              name="visibility"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.visibility} error={fieldState.error} isOptional>
                  {({ labelId }) => (
                    <VisibilityScope
                      {...field}
                      inputId={labelId}
                      isDisabled={isSubmitting}
                      onChange={(value) => setValue("visibility", value)}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="order"
              control={control}
              rules={{ required: true, min: 1 }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.order} error={fieldState.error}>
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      {...field}
                      id={labelId}
                      isInvalid={isInvalid}
                      decimalScale={0}
                      allowNegative={false}
                      disabled={isSubmitting}
                      onBlur={(value) => setValue("order", value)}
                    />
                  )}
                </FormField>
              )}
            />
          </FormFieldsContainer>
        );
      }}
    </Form>
  );
};

export default NewNormative;
